import { useState, useRef, useEffect } from 'react'
import { MessageCircle, ChevronDown, Send, X, ArrowDownCircle } from 'react-feather'
import liscrLogo from '../../assets/images/liscr_logo.png'
import favicon from '../../assets/images/liscerfavicon.png'
import GlobalVariable from '../../settings/global'
import axios from 'axios'

const ChatbotWidget = () => {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: 'Welcome!<br/>Thank you for connecting with us. We are here to assist you. Please share your questions or requests.',
      isHtml: true
    }
  ])

  const messagesEndRef = useRef(null)
  const chatBodyRef = useRef(null)

  /** DRAGGABLE PANEL **/
  const panelRef = useRef(null)
  const pos = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0, dragging: false })

  const startDrag = (e) => {
    const panel = panelRef.current
    pos.current.dragging = true
    pos.current.offsetX = e.clientX - panel.offsetLeft
    pos.current.offsetY = e.clientY - panel.offsetTop
  }

  const duringDrag = (e) => {
    if (!pos.current.dragging) return
    const panel = panelRef.current
    panel.style.left = `${e.clientX - pos.current.offsetX}px`
    panel.style.top = `${e.clientY - pos.current.offsetY}px`
  }

  const stopDrag = () => {
    pos.current.dragging = false
  }

  useEffect(() => {
    window.addEventListener('mousemove', duringDrag)
    window.addEventListener('mouseup', stopDrag)
    return () => {
      window.removeEventListener('mousemove', duringDrag)
      window.removeEventListener('mouseup', stopDrag)
    }
  }, [])

  /*** SCROLL + CHAT BOT LOGIC ***/

  useEffect(() => {
    if (!open) return
    const chatBody = chatBodyRef.current
    if (!chatBody) return
    const handleScroll = () => {
      const isAtBottom =
        chatBody.scrollTop + chatBody.clientHeight >= chatBody.scrollHeight - 10
      setShowScrollButton(!isAtBottom)
    }
    chatBody.addEventListener('scroll', handleScroll)
    return () => chatBody.removeEventListener('scroll', handleScroll)
  }, [open])

  const handleSend = async () => {
    const trimmed = input.trim()
    if (!trimmed) return

    setMessages(prev => [...prev, { type: 'user', text: trimmed }])
    setInput('')
    setIsTyping(true)

    try {
      const response = await axios.post(
        `${GlobalVariable.hostpath}api/Common/AskMe`,
        { question: trimmed }
      )

      const botResponse =
        response?.data?.data?.response ||
        '✅ Received your message, but no reply was found.'

      setIsTyping(false)

      setMessages(prev => [
        ...prev,
        {
          type: 'bot',
          text: botResponse.replace(/\n/g, "<br/>"),
          isHtml: true
        }
      ])
    } catch (error) {
      setMessages(prev => [
        ...prev,
        {
          type: 'bot',
          text: '⚠️ Unable to reach server. Please try again later.',
          isHtml: false
        }
      ])
      setIsTyping(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSend()
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  return (
    <div className="chatbot-widget d-none">
      <button
        className="chatbot-toggle"
        onClick={() => setOpen(!open)}
        aria-label="Toggle Chatbot"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div
          className="chatbot-panel draggable"
          ref={panelRef}
          style={{ position: 'fixed', right: '35px', bottom: '118px', maxHeight: '531px' }}
        >
          <div
            className="chatbot-header drag-handle"
            onMouseDown={startDrag}
            style={{ cursor: 'move' }}
          >
            <img src={liscrLogo} alt="Liberian Registry" className="chatbot-logo" />
            <ChevronDown onClick={() => setOpen(false)} className="chatbot-close-icon chatbotheaderClear" />
          </div>

          <div className="chatbot-body" ref={chatBodyRef}>
            {messages.map((msg, index) => (
              <div key={index} className={`chat-message ${msg.type}`}>
                {msg.type === 'bot' && <img src={favicon} alt="bot" className="chat-avatar" />}
                <div className={`chat-bubble ${msg.type}`}>
                  {msg.isHtml ? (
                    <div dangerouslySetInnerHTML={{ __html: msg.text }} />
                  ) : msg.text}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="chat-message bot">
                <img src={favicon} alt="bot" className="chat-avatar" />
                <div className="chat-bubble bot typing-indicator">
                  <span className="dot" /><span className="dot" /><span className="dot" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {showScrollButton && (
            <button
              className="scroll-to-bottom-btn"
              onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
            >
              <ArrowDownCircle size={20} />
            </button>
          )}

          <div className="chatbot-input">
            <input
              type="text"
              placeholder="Message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button onClick={handleSend}>
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatbotWidget
